import React, { PropsWithChildren } from 'react'
import { createContext } from 'lib/createContext'
import { parseErrorMessage } from 'api'
import {
  useCustomShiftNotificationsQuery,
  useDeleteCustomShiftNotificationMutation,
} from 'queries/shifts'
import { useShiftDetailContextValue } from '../../../context/ShiftDetailProvider'
import { useShiftDetailWorkflowsContextValue } from '../../../context/ShiftDetailWorkflowsProvider'
import { GetCustomShiftNotificationsResponse } from 'api/shifts'

type CustomShiftNotificationsContextState = {
  state: {
    notifications: NonNullable<GetCustomShiftNotificationsResponse>['notifications']
    isPending: boolean
    isError: boolean
    deleteMutationState: {
      isPending: boolean
      isError: boolean
      errorMessage: string | undefined
    }
  }
  actions: {
    refetch: () => void
    openCreate: () => void
    remove: ReturnType<
      typeof useDeleteCustomShiftNotificationMutation
    >['mutate']
  }
}

const [Context, useCustomShiftNotificationsContextValue] =
  createContext<CustomShiftNotificationsContextState>({
    name: 'CustomShiftNotificationsContext',
  })

export const CustomShiftNotificationsProvider = ({
  children,
}: PropsWithChildren) => {
  const {
    state: { id: shiftId },
  } = useShiftDetailContextValue()
  const {
    createCustomShiftNotificationFlow: { open: openCreate },
  } = useShiftDetailWorkflowsContextValue()

  const { data, isPending, isError, refetch } =
    useCustomShiftNotificationsQuery(shiftId)
  const {
    mutate: remove,
    isPending: isDeletePending,
    isError: isDeleteError,
    error: deleteError,
  } = useDeleteCustomShiftNotificationMutation(shiftId)

  const value: CustomShiftNotificationsContextState = {
    state: {
      notifications: data?.notifications ?? [],
      isPending,
      isError,
      deleteMutationState: {
        isPending: isDeletePending,
        isError: isDeleteError,
        errorMessage: parseErrorMessage(deleteError),
      },
    },
    actions: {
      refetch,
      openCreate,
      remove,
    },
  }

  return <Context.Provider value={value}>{children}</Context.Provider>
}

export { useCustomShiftNotificationsContextValue }
